import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NextSeo } from "next-seo";
import moment, { Moment } from "moment-timezone";
import { DropResult, DraggableLocation } from "react-beautiful-dnd";
import { DEFAULT_BOOK_ID, NameKeyPair } from "@rotoql/common-services";
import dynamic from "next/dynamic";
import { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/router";
import Joyride, { CallBackProps } from "react-joyride";
import Icon from "@mdi/react";
import { Input } from "reactstrap";
import { mdiClock, mdiClockOutline, mdiStar, mdiStarOutline, mdiFolderOutline } from "@mdi/js";
import _ from "lodash";
import { ApolloQueryResult, useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { isMobile } from "react-device-detect";
import useAsyncEffect from "use-async-effect";

import { initializeApollo } from "src/lib/apollo-client";
import { SubscriptionContext } from "src/services/SubscriptionService";
import DatePicker from "src/components/DatePicker";
import Dropdown from "src/components/Dropdown";
import DropdownWithButtons from "src/components/DropdownWithButtons";
import ButtonGroup from "src/components/ButtonGroup";
import { reorder, getColumnSort } from "src/components/GamesTable/ColumnService";
import { getSEOTitle, getSEODescription, getSEOH1, getSEOImage } from "src/components/SEO";
import ModifyStatsDropdown from "src/components/GamesTable/ModifyStatsDropdown";

import {
  EVENTS_PICKS_QUERY,
  GAMES_TABLE_SEO_QUERY,
  PAID_LANDING_PAGES_SEO_QUERY,
  USER_UI_SETTINGS_AND_ALL_BOOKS,
} from "src/gql/queries";
import { CREATE_USER_UI_SETTING } from "src/gql/mutations";
import {
  PAGES,
  WEB_SPORTS_MAP,
  Sportsbook,
  COLUMN_VALUE_RATING,
  COLUMN_PUBLIC_TICKET_PCT,
  COLUMN_PRO_MONEY_EDGE,
  COLUMN_LINE_MOVE,
  DESKTOP_STEPS,
  MOBILE_STEPS,
  COLUMN_CONSENSUS,
  COLUMN_PITCHERS,
  COLUMN_HOME_RECORD,
  COLUMN_ROAD_RECORD,
  COLUMN_HOME_ATS_RECORD,
  COLUMN_ROAD_ATS_RECORD,
  COLUMN_HOME_OU_RECORD,
  COLUMN_ROAD_OU_RECORD,
  COLUMN_INJURIES,
  DEFAULT_TIMEZONE,
  PPCSportsList,
  PPCPagesList,
} from "src/constants";
import { ScheduleItem, getSchedule } from "src/services/ScheduleService";
import { generateEventDisplayProps } from "src/services/EventService";
import { getDefaultSelectedDate, getScheduleIndex } from "src/services/DateService";
import AnalyticsService from "src/services/AnalyticsService";
import styles from "src/styles/games.scss";
import LiveGameBar from "src/components/LiveGameBar";
import GamesRail from "src/components/GamesRail";
import { renderRichText } from "src/services/PrismicService";
import { isPpcPage, noIndexPages } from "src/services/NoIndexService";
import OfferCarouselModal from "src/components/OfferCarouselModal";
import Drawer, { DrawerHandle } from "src/components/GamesTable/Drawer";
import { getStringArrayQueryParam, getStringQueryParam } from "src/services/UrlService";
import { BookIdSetting, ColumnOrderSetting, GamesViewProps, PageItem } from "src/typings/[sport]/[...page]";
import { buildStaticPathsForSportsBettingPage } from "src/services/StaticPathsService";
import { EUROPEAN_SOCCER_EVENTS } from "src/gql/queries/european_soccer_events";
import { isEuropeanSoccerLeague } from "src/services/SportService";
import { getConferenceSchedule, hasConfiguredConferenceSchedule } from "src/services/ScheduleService";
import { useUser } from "src/services/UserService";
import { customOfferModalOpen } from "src/services/ApolloService";

const GamesTable = dynamic({
  loader: () => import("src/components/GamesTable"),
  ssr: false,
});

const BetPageDescriptions: PageItem[] = [
  {
    title: "Odds",
    name: "Model Bets",
    key: "value",
    enum: "VALUEBETS",
    path: "odds",
    description:
      "We combine the top trends, sharp, and recent line moves. Ratings will post final update in late afternoon.",
    description2: "5 stars = great best bet right now",
    sortColumnId: COLUMN_VALUE_RATING,
  },
  {
    title: "Trending Bets",
    name: "Trending Bets",
    key: "trend",
    enum: "TRENDBETS",
    path: "best-bets",
    description:
      "We combine the top trends, sharp, and recent line moves. Ratings will post final update in late afternoon.",
    description2: "5 stars = great best bet right now",
    sortColumnId: COLUMN_CONSENSUS,
  },
  {
    title: "Public Betting",
    name: "Public Bets",
    key: "public",
    enum: "PUBLICBETS",
    path: "public-betting",
    description:
      "What are the most popular bets today? Going against picks >70% for spread bets is usually better over the long haul.",
    sortColumnId: COLUMN_PUBLIC_TICKET_PCT,
  },
  {
    title: "Sharp Picks",
    name: "Sharp Picks",
    key: "sharp",
    enum: "SHARPBETS",
    path: "sharp-picks",
    description:
      "Follow professional bettors by comparing where the most money has been placed vs. amount of bets placed.",
    sortColumnId: COLUMN_PRO_MONEY_EDGE,
  },
  {
    title: "Line Movement",
    name: "Line Movement",
    key: "movement",
    enum: "LINEMOVEMENT",
    path: "line-movement",
    description:
      "See which games the sportsbook initially mispriced. We subtract the difference between current line and the first line released (open).",
    // This will get overwritten in setPage/setFilter functions since Line Move is multiple columns
    sortColumnId: COLUMN_LINE_MOVE,
  },
];

/** @description Get SEO content for page from Prismic response  */
const getSEOContent = (
  seoContentResponse: ApolloQueryResult<PrismicGamesTableContent | PrismicPaidLandingPageContent>,
  pageKey: string,
  filterKey: string | undefined
) => {
  const seoForPPCPage = pageKey.endsWith("ppc") || filterKey?.endsWith("ppc");
  pageKey = pageKey.replace("-ppc", "");
  filterKey = filterKey?.replace("-ppc", "");

  let seoContent;
  if (seoForPPCPage) {
    if ("prismicPaid_landing_pages" in seoContentResponse?.data) {
      seoContent = seoContentResponse?.data?.prismicPaid_landing_pages?.seo_content;
    }
  } else {
    if ("prismicGames_table" in seoContentResponse?.data) {
      seoContent = seoContentResponse?.data?.prismicGames_table?.seo_content;
    }
  }

  if (!seoContent) {
    return null;
  }

  const options = _.sortBy(seoContent, [
    (o: any) => {
      if (o.bet_category === "ALL" && o.bet_type === "ALL") {
        return 5;
      }
      if (o.bet_category === "ALL" || o.bet_type === "ALL") {
        return 4;
      }
      if (o.bet_category === "DEFAULT" && o.bet_type === "DEFAULT") {
        return 3;
      }
      if (o.bet_category === "DEFAULT" || o.bet_type === "DEFAULT") {
        return 2;
      }
      return 1;
    },
  ]);

  const bestOption = options.find((o: any) => {
    let betTypeMatch = false;
    let betCatMatch = false;
    if (o.bet_type === "ALL") {
      betTypeMatch = true;
    }
    if (o.bet_category === "ALL") {
      betCatMatch = true;
    }
    if (!filterKey && o.bet_type === "DEFAULT") {
      betTypeMatch = true;
    }
    if (!pageKey && o.bet_category === "DEFAULT") {
      betCatMatch = true;
    }
    if (filterKey === o.bet_type) {
      betTypeMatch = true;
    }
    if (pageKey === o.bet_category) {
      betCatMatch = true;
    }
    return betTypeMatch && betCatMatch;
  });

  return bestOption ?? null;
};

const isDefaultDashboard = (settingName: string) => {
  return (
    settingName === "Default Best Bets" ||
    settingName === "Default Trend Bets" ||
    settingName === "Default Public Bets" ||
    settingName === "Default Sharp Picks" ||
    settingName === "Default Line Movement"
  );
};

export const DEFAULT_DASH_NAME = "Click to name this dashboard";
const DEFAULT_BOOK: Sportsbook = {
  id: DEFAULT_BOOK_ID,
  name: "Consensus",
  rank: 0,
};

/**
 * This is the main Games Page of the app, it houses the table and filtering
 */
const Games = ({ seoContent, sportUrl, pageUrl, betTypeUrl, conferenceUrl, schedule }: GamesViewProps) => {
  const currentSportObj = WEB_SPORTS_MAP[sportUrl];
  const selectedPageIndex = PAGES[pageUrl] ?? 0;
  const selectedPage = BetPageDescriptions[selectedPageIndex];
  const ctx = useContext(SubscriptionContext);
  const user = useUser();
  const customOfferModalShow = useReactiveVar(customOfferModalOpen);
  const drawerRef = useRef<DrawerHandle>();
  const { asPath, query } = useRouter();
  // The URL has a specific date it wants to render
  let initDate = getDefaultSelectedDate(currentSportObj.lookupKey).toISOString();
  if (typeof query?.date === "string") {
    const tempDate = moment.tz(query?.date, DEFAULT_TIMEZONE);
    if (tempDate.isValid()) {
      initDate = query?.date;
    }
  }

  // The URL has a specific schedule index it wants to render
  let initDateIndex: number | null = null;
  if (currentSportObj.hasConfiguredSchedule && typeof query?.dateindex === "string") {
    const tempDateIndex = parseInt(query.dateindex, 10);
    if (!isNaN(tempDateIndex) && tempDateIndex >= 0 && tempDateIndex <= schedule.length - 1) {
      initDateIndex = tempDateIndex;
    }
  }

  // Settings
  const [unsavedBookId, setUnsavedBookId] = useState<string | null>(null);
  const [unsavedColumnOrder, setUnsavedColumnOrder] = useState<number[] | null>(null);
  const [unsavedDashboardName, setUnsavedDashboardName] = useState<string | null>(null);
  const [unsavedSettings, setUnsavedSettings] = useState<boolean>(false);
  // Display
  const [selectedSortColumnId, setSelectedSortColumnId] = useState<number>(selectedPage.sortColumnId);
  const [columnSortDesc, setColumnSortDesc] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<Moment>(moment.tz(initDate, DEFAULT_TIMEZONE));
  const [selectedDateIndex, setSelectedDateIndex] = useState<number | null>(initDateIndex);
  const [showProductTour, setShowProductTour] = useState<boolean>(false);
  // Mobile
  const [mobileSortIndex, setMobileSortIndex] = useState<number>(1);
  const [mobileSearchFilter, setMobileSearchFilter] = useState<string>("");

  // Set user's TZ initially
  useEffect(() => {
    setSelectedDate(moment.tz(selectedDate, Intl.DateTimeFormat().resolvedOptions().timeZone));
  }, []);

  // Show social Ad if needed
  useEffect(() => {
    if (!ctx.signedIn && typeof query?.social === "string") {
      setTimeout(() => ctx.openLoginModal(4), 2000);
      AnalyticsService.track("User Clicked On Social Ad", {
        screen: "Social Landing Page",
        social: query.social,
      });
    }
  }, []);

  // Reset defaults when sportKey changes
  useEffect(() => {
    setUnsavedColumnOrder(null);
    setUnsavedBookId(null);
    setUnsavedSettings(false);
  }, [sportUrl]);

  // Reset defaults when pageKey changes
  useEffect(() => {
    setSelectedSortColumnId(selectedPage.sortColumnId);
    setColumnSortDesc(true);
    setUnsavedColumnOrder(null);
    setUnsavedBookId(null);
    setUnsavedSettings(false);
  }, [pageUrl]);

  const {
    loading: settingsLoading,
    error: settingsError,
    data: settingData,
  } = useQuery(USER_UI_SETTINGS_AND_ALL_BOOKS, {
    variables: {
      league: currentSportObj.key,
      page: selectedPage.enum,
    },
    context: { debatch: true },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  const settingsData = _.cloneDeep(settingData);

  const adEvents = {
    valueEvents: settingsData?.paywall?.valueEvents ?? 0,
    sharpEvents: settingsData?.paywall?.sharpEvents ?? 0,
    message: settingsData?.appMessaging?.message ?? null,
  };

  const userBookSetting: BookIdSetting = settingsData?.bookSetting ?? {
    parsedValue: DEFAULT_BOOK_ID,
    id: "default-book",
  };

  const selectedBookId = userBookSetting.parsedValue ?? DEFAULT_BOOK_ID;

  const bookList: Sportsbook[] = settingsData?.books ?? [];
  const selectedBook = useMemo(() => {
    const tempBook = bookList.find((b) => b.id === selectedBookId);
    return tempBook ?? DEFAULT_BOOK;
  }, [bookList, selectedBookId]);

  const userColumnOrderSetting: ColumnOrderSetting = useMemo(
    () =>
      settingsData?.columnOrder ?? {
        id: "default-column-order",
        parsedValue: JSON.parse(currentSportObj.defaultUserSetting),
        uiSection: selectedPage.enum,
        updateTs: moment.utc().format(),
      },
    [settingsData?.columnOrder, selectedPage.enum, currentSportObj.defaultUserSetting]
  );

  const settingList = useMemo(() => {
    const list: ColumnOrderSetting[] = settingsData?.columnOrderList ?? [];
    list.sort((a: ColumnOrderSetting, b: ColumnOrderSetting) => {
      if (a.updateTs !== null && b.updateTs === null) {
        return -1;
      }
      if (a.updateTs === null && b.updateTs !== null) {
        return 1;
      }
      if (moment(a.updateTs).isAfter(b.updateTs)) {
        return -1;
      }
      if (moment(a.updateTs).isBefore(b.updateTs)) {
        return 1;
      }
      return 0;
    });
    return list;
  }, [settingsData?.columnOrderList]);

  const selectedColumnOrder = useMemo(() => {
    let tempOrder = userColumnOrderSetting?.parsedValue?.columnOrder ?? [];

    // Show/Hide pitchers column only if we should
    const pitchersIndex = tempOrder.indexOf(COLUMN_PITCHERS);
    if (currentSportObj.hasPitchers && pitchersIndex === -1) {
      tempOrder = [COLUMN_PITCHERS, ...tempOrder];
    } else if (!currentSportObj.hasPitchers && pitchersIndex !== -1) {
      tempOrder.splice(pitchersIndex, 1);
    }

    // Show/Hide injuries column only if we should
    if (!currentSportObj.hasNewsInjuriesColumn) {
      const injuriesIndex = tempOrder.indexOf(COLUMN_INJURIES);
      if (injuriesIndex !== -1) {
        tempOrder.splice(injuriesIndex, 1);
      }
    }

    if (currentSportObj.key === "NFL" || currentSportObj.key === "CFB" || currentSportObj.key === "XFL") {
      tempOrder = tempOrder.filter((columnNumber: number) => {
        return (
          // This is a hack currently as we don't support these column values currently,
          // we will revert this in future
          columnNumber !== COLUMN_HOME_RECORD &&
          columnNumber !== COLUMN_ROAD_RECORD &&
          columnNumber !== COLUMN_HOME_ATS_RECORD &&
          columnNumber !== COLUMN_ROAD_ATS_RECORD &&
          columnNumber !== COLUMN_HOME_OU_RECORD &&
          columnNumber !== COLUMN_ROAD_OU_RECORD
        );
      });
    }

    return tempOrder;
  }, [userColumnOrderSetting]);

  const activeBookId = unsavedBookId ?? selectedBookId;
  const activeColumnOrder = unsavedColumnOrder ?? selectedColumnOrder;
  const tempDashboardName = unsavedDashboardName ?? userColumnOrderSetting.parsedValue.name;
  const activeDashboardName = isDefaultDashboard(tempDashboardName) ? DEFAULT_DASH_NAME : tempDashboardName;

  // Update conference ID if conferenceKey changes
  const { selectedConference, selectedConferenceIndex, selectedConferenceSchedule, hasConferenceSchedule } =
    useMemo(() => {
      let tempIndex, tempConference;
      if (currentSportObj.conferenceList) {
        const defaultConference = currentSportObj.conferenceList ? currentSportObj.conferenceList[0] : null;
        tempIndex = currentSportObj.conferenceList.findIndex((conference) => conference.url === conferenceUrl);
        if (tempIndex < 0) {
          tempIndex = 0;

          if (defaultConference?.key === "NCAAT") {
            tempConference = defaultConference;
          }
        } else {
          tempConference = currentSportObj.conferenceList[tempIndex];
        }
      }
      return {
        selectedConference: tempConference,
        selectedConferenceIndex: tempIndex,
        selectedConferenceSchedule: getConferenceSchedule(tempConference?.key),
        hasConferenceSchedule: hasConfiguredConferenceSchedule(tempConference?.key),
      };
    }, [sportUrl, conferenceUrl]);

  const currentSchedule = hasConferenceSchedule ? selectedConferenceSchedule ?? [] : schedule;
  const hasValidConfiguredSchedule =
    (currentSportObj.hasConfiguredSchedule || hasConferenceSchedule) && currentSchedule.length > 0;
  const isEuropeanSoccerLeagueWithSchedule = isEuropeanSoccerLeague(currentSportObj) && hasValidConfiguredSchedule;

  const { selectedWeekIndex, after, before } = useMemo<{
    selectedWeekIndex?: number;
    after: Moment;
    before: Moment;
  }>(() => {
    if (hasValidConfiguredSchedule) {
      let weekIndex = 0;
      let a = moment(currentSchedule[0].startDate);
      let b = moment(currentSchedule[0].endDate);
      if (selectedDateIndex === null) {
        weekIndex = getScheduleIndex(currentSchedule);
        a = moment(currentSchedule[weekIndex].startDate);
        b = moment(currentSchedule[weekIndex].endDate);
      } else {
        weekIndex = selectedDateIndex;
        a = moment(currentSchedule[selectedDateIndex].startDate);
        b = moment(currentSchedule[selectedDateIndex].endDate);
      }
      return {
        selectedWeekIndex: weekIndex,
        after: a.clone().startOf("day"),
        before: b.clone().endOf("day"),
      };
    } else {
      // Get the start and end of the day for the selectedDate to use as
      // our before and after dates on the query.
      return {
        after: selectedDate.clone().startOf("day").utc(),
        before: selectedDate.clone().endOf("day").utc(),
      };
    }
  }, [sportUrl, selectedDateIndex, selectedDate, conferenceUrl]);

  // Update filter ID if filterKey changes
  const { selectedBetType, selectedBetTypeIndex } = useMemo(() => {
    let tempIndex = currentSportObj.filters.findIndex((betType) => betType.url === betTypeUrl);
    if (tempIndex < 0) {
      tempIndex = 0;
    }
    return {
      selectedBetType: currentSportObj.filters[tempIndex],
      selectedBetTypeIndex: tempIndex,
    };
  }, [sportUrl, betTypeUrl]);

  let conferenceEnum;
  if (
    (conferenceUrl && conferenceUrl.toLowerCase() !== "all") ||
    (!conferenceUrl && selectedConference?.key.toLowerCase() === "ncaat")
  ) {
    conferenceEnum = selectedConference?.key;
  }

  const searchVariablesForEventsQuery = isEuropeanSoccerLeagueWithSchedule
    ? { slugIds: schedule[selectedWeekIndex || 0]?.matchupSlugIds }
    : {
        conference: conferenceEnum,
        before,
        after,
      };

  const EVENTS_QUERY = isEuropeanSoccerLeagueWithSchedule ? EUROPEAN_SOCCER_EVENTS : EVENTS_PICKS_QUERY;

  const {
    loading: eventsLoading,
    error: eventsError,
    data: eventsData,
  } = useQuery(EVENTS_QUERY, {
    variables: {
      ...searchVariablesForEventsQuery,
      league: currentSportObj.key,
      lineType: ["CURRENT", "OPEN"],
      period: currentSportObj.periods,
      books: [activeBookId],
      book: activeBookId,
      hasTeamPowerProjectionGrades: currentSportObj.hasTeamPowerProjectionGrades,
      hasFGspreadPowerRating: currentSportObj.hasFGspreadPowerRating,
      hasFGmoneylinePowerRating: currentSportObj.hasFGmoneylinePowerRating,
      hasFGtotalPowerRating: currentSportObj.hasFGtotalPowerRating,
      has1HspreadPowerRating: currentSportObj.has1HspreadPowerRating,
      has1HmoneylinePowerRating: currentSportObj.has1HmoneylinePowerRating,
      has1HtotalPowerRating: currentSportObj.has1HtotalPowerRating,
      has1PspreadPowerRating: currentSportObj.has1PspreadPowerRating,
      has1PmoneylinePowerRating: currentSportObj.has1PmoneylinePowerRating,
      has1PtotalPowerRating: currentSportObj.has1PtotalPowerRating,
      hasPitchers: currentSportObj.hasPitchers,
      hasInjuries: currentSportObj.hasInjuries,
      isNBA: currentSportObj.name === "NBA",
    },
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
    skip: !settingsData,
  });

  const filterItems = (items: any) => {
    return mobileSearchFilter === ""
      ? items
      : items.filter((item: any) => {
          return (
            (item.homeTeam &&
              ((item.homeTeam.fullName &&
                item.homeTeam.fullName.toUpperCase().includes(mobileSearchFilter.toUpperCase())) ||
                (item.homeTeam.preferredAbbreviation &&
                  item.homeTeam.preferredAbbreviation.toUpperCase().includes(mobileSearchFilter.toUpperCase())))) ||
            (item.awayTeam &&
              ((item.awayTeam.fullName &&
                item.awayTeam.fullName.toUpperCase().includes(mobileSearchFilter.toUpperCase())) ||
                (item.awayTeam.preferredAbbreviation &&
                  item.awayTeam.preferredAbbreviation.toUpperCase().includes(mobileSearchFilter.toUpperCase()))))
          );
        });
  };

  const events = useMemo(() => {
    const currentEvents = isEuropeanSoccerLeagueWithSchedule ? eventsData?.gameweekEventsBySlugIds : eventsData?.events;

    let tempEvents =
      currentEvents?.map((event: any) =>
        generateEventDisplayProps({
          event,
          currentSport: currentSportObj,
          currentBookId: activeBookId,
          filterKey: selectedBetType.key,
        })
      ) ?? [];
    // Filter items if necassary on mobile
    tempEvents = mobileSearchFilter !== "" ? filterItems(tempEvents) : tempEvents;

    // TODO simplify this sorting
    if (mobileSortIndex === 1) {
      if (currentSportObj.key !== "XFL" && (!ctx.signedIn || !ctx.hasSubscriptionAccess(currentSportObj.key))) {
        tempEvents.sort((eventA: any, eventB: any) => {
          // move postponed games to end of the list
          if (eventA.eventState !== "POSTPONED" && eventB.eventState === "POSTPONED") {
            return -1;
          }
          if (eventA.eventState === "POSTPONED" && eventB.eventState !== "POSTPONED") {
            return 1;
          }
          if (eventA.freeGame && !eventB.freeGame) {
            return -1;
          }
          if (!eventA.freeGame && eventB.freeGame) {
            return 1;
          }
          // move past games to end of the list
          if (
            (eventA.eventState === "STARTED" || eventA.eventState === "FINAL") &&
            eventB.eventState !== "STARTED" &&
            eventB.eventState !== "FINAL"
          ) {
            return 1;
          }
          if (
            eventA.eventState !== "STARTED" &&
            eventA.eventState !== "FINAL" &&
            (eventB.eventState === "STARTED" || eventB.eventState === "FINAL")
          ) {
            return -1;
          }
          if (eventA.freeGame && eventB.freeGame) {
            return getColumnSort(
              selectedSortColumnId,
              eventA,
              eventB,
              selectedBetType.key,
              currentSportObj,
              columnSortDesc
            );
          } else {
            return 0;
          }
        });
      } else {
        tempEvents.sort((eventA: any, eventB: any) => {
          // move postponed games to end of the list
          if (eventA.eventState !== "POSTPONED" && eventB.eventState === "POSTPONED") {
            return -1;
          }
          if (eventA.eventState === "POSTPONED" && eventB.eventState !== "POSTPONED") {
            return 1;
          }
          // move past games to end of the list
          if (
            (eventA.eventState === "STARTED" || eventA.eventState === "FINAL") &&
            eventB.eventState !== "STARTED" &&
            eventB.eventState !== "FINAL"
          ) {
            return 1;
          }
          if (
            eventA.eventState !== "STARTED" &&
            eventA.eventState !== "FINAL" &&
            (eventB.eventState === "STARTED" || eventB.eventState === "FINAL")
          ) {
            return -1;
          }
          return getColumnSort(
            selectedSortColumnId,
            eventA,
            eventB,
            selectedBetType.key,
            currentSportObj,
            columnSortDesc
          );
        });
      }
    }

    return tempEvents;
  }, [
    selectedWeekIndex,
    eventsData,
    activeBookId,
    mobileSortIndex,
    mobileSearchFilter,
    selectedSortColumnId,
    columnSortDesc,
    selectedBetType.key,
  ]);

  // Generate SEO values
  const defaultTitle = `${currentSportObj.name} ${selectedPage.title} ${selectedBetType.name} ${
    selectedConference?.name ?? ""
  }`;
  const pageTitle =
    seoContent?.seo_title ?? getSEOTitle(sportUrl, pageUrl, betTypeUrl, conferenceUrl, `${defaultTitle} | BetQL`);
  const pageDescription = seoContent?.description ?? getSEODescription(sportUrl, pageUrl, betTypeUrl, conferenceUrl);
  const pageH1 = seoContent?.h1 ?? getSEOH1(sportUrl, pageUrl, betTypeUrl, conferenceUrl, defaultTitle);
  const pageHeadline = seoContent?.headline;
  const ogImage = seoContent?.image ?? getSEOImage(sportUrl, pageUrl, betTypeUrl, conferenceUrl);
  const noindex = Boolean(conferenceUrl || noIndexPages.includes(asPath) || isPpcPage(asPath));

  // Mutations
  const [createUserUISetting] = useMutation(CREATE_USER_UI_SETTING);

  useAsyncEffect(async () => {
    const walkthroughComplete = settingsData?.walkthroughComplete;
    if (walkthroughComplete) {
      const walkthroughSettingValue = walkthroughComplete.parsedValue;
      // the component updates after createUser causing this to run
      // before the query has a chance to refetch as a signed in user
      if (ctx.signedIn && !walkthroughSettingValue && user?.selectedBooksOn && !customOfferModalShow) {
        setShowProductTour(true);
      } else if (!ctx.signedIn) {
        setShowProductTour(false);
      }
    }
  }, [ctx.signedIn, settingsData?.walkthroughComplete, user?.selectedBooksOn, customOfferModalShow]);

  /** The pageUrl is set by a URL param, so this function tracks the click */
  const onSelectPage = useCallback(
    (page: PageItem) =>
      AnalyticsService.track("Change Page", {
        screen: "FeedView",
        page: page.key,
        betType: selectedBetType.key,
        sport: sportUrl,
        book: selectedBook.name,
      }),
    [sportUrl, selectedBetType.key, selectedBook.name]
  );

  /** The betType is set by a URL param, so this function tracks the click */
  const onSelectBetType = useCallback(
    (betType: NameKeyPair) => {
      AnalyticsService.track("Change Filter", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: betType.key,
        sport: sportUrl,
        book: selectedBook.name,
      });
    },
    [sportUrl, selectedPage.key, selectedBook.name]
  );

  // Schedule Selector
  /** Move to the next item in the schedule  */
  const onSelectRightChevron = useCallback(
    (dateIndex: number) => {
      setSelectedDateIndex(dateIndex ? dateIndex + 1 : 1);
      AnalyticsService.track("Change Week Schedule From Right Arrow", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: selectedBetType.name,
        sport: sportUrl,
      });
    },
    [sportUrl, selectedPage.key, selectedBetType.name]
  );

  /** Move to the previous item in the schedule */
  const onSelectLeftChevron = useCallback(
    (dateIndex: number) => {
      setSelectedDateIndex(dateIndex ? dateIndex - 1 : 0);
      AnalyticsService.track("Change Week Schedule From Left Arrow", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: selectedBetType.key,
        sport: sportUrl,
      });
    },
    [sportUrl, selectedPage.key, selectedBetType.key]
  );

  /** Select a specific item in the schedule */
  const onSchduleSelection = useCallback(
    (selectedScheduleWeek: ScheduleItem, selectedSchduleIndex: number) => {
      setSelectedDateIndex(selectedSchduleIndex);
      AnalyticsService.track("Change Week Schedule", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: selectedBetType.key,
        sport: sportUrl,
        selectedScheduleWeek,
      });
    },
    [sportUrl, selectedPage.key, selectedBetType.key]
  );

  // Date selector
  /**
   * This function updates the current date being used to display the values
   * in the table.
   *
   * @param date the new selected moment;
   */
  const onSelectedDate = useCallback(
    (date: Moment) => {
      setSelectedDate(date);
      AnalyticsService.track("Change Date", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: selectedBetType.key,
        sport: sportUrl,
        book: selectedBook.name,
      });
    },
    [sportUrl, selectedPage.key, selectedBetType.key, selectedBook.name]
  );

  // ModifyStatsDropdown
  /**
   * Track when a user cancels the ModifyStatsDropdown
   */
  const onCancelChanges = useCallback(
    () =>
      AnalyticsService.track("Add / Remove Stats Cancel", {
        screen: "FeedView",
        page: selectedPage.key,
        betType: selectedBetType.key,
        sport: sportUrl,
        book: selectedBook.name,
      }),
    [sportUrl, selectedPage.key, selectedBetType.key, selectedBook.name]
  );

  /** Save the changes from ModifyStatsDropdown */
  const onSaveChanges = useCallback(
    (bookId: string, columnOrder: number[]) => {
      setUnsavedBookId(bookId);
      setUnsavedColumnOrder(columnOrder);
      setUnsavedSettings(true);
      AnalyticsService.track("Add / Remove Stats Save", {
        screen: "FeedView",
        page: selectedPage.key,
        sport: sportUrl,
        book: bookId,
        settings: columnOrder,
        numColumns: columnOrder.length,
      });
    },
    [sportUrl, selectedPage.key]
  );

  // Drawer
  /** Toggle the drawer open and closed using dynamc screenwidth? */
  const openDrawer = useCallback(() => drawerRef.current?.toggleDrawer(), [drawerRef]);
  /** Set the temp dashboard name after a user changes it */
  const setDashName = useCallback((name: string | null) => setUnsavedDashboardName(name), []);
  /** Reset unsaved values after saving a new setting */
  const saveSetting = useCallback(() => {
    setUnsavedColumnOrder(null);
    setUnsavedBookId(null);
    setUnsavedDashboardName(null);
    setUnsavedSettings(false);
  }, []);
  /** Reset unsaved values after updating the current setting */
  const updateSetting = useCallback(() => {
    setUnsavedBookId(null);
    setUnsavedColumnOrder(null);
    setUnsavedSettings(false);
  }, []);
  /** Reset the unsaved dash name when we delete a setting to go back to our selected name */
  const deleteSetting = useCallback(() => setUnsavedDashboardName(null), []);
  /** Set the selected dash name when we select a setting preset */
  const selectSetting = useCallback(() => {
    setUnsavedColumnOrder(null);
    setUnsavedBookId(null);
    setUnsavedDashboardName(null);
    setUnsavedSettings(false);
  }, []);

  // Drag and Drop
  /**
   * This function will reorder the columnOrder list so that we get the correct ordering after
   * the drag and drop
   *
   * @param result this is the result containing the start and end indexs of the dragged element
   * @param columnOrder: number[] The columnOrder used to track the current
   *  columns being displayed, and what order they are in. This propery controls the
   *  settings LargeDropdown as well as the displayed gamesTable columns
   */
  const onDragEnd = useCallback(
    (result: DropResult) => {
      // dropped nowhere
      if (!result.destination) {
        return;
      }

      const source: DraggableLocation = result.source;
      const destination: DraggableLocation = result.destination;

      // did not move anywhere - can bail early
      if (source.droppableId === destination.droppableId && source.index === destination.index) {
        return;
      }

      // reordering column
      if (result.type === "COLUMN") {
        const sortedColumnOrder: number[] = reorder(activeColumnOrder, source.index, destination.index);

        setUnsavedSettings(selectedColumnOrder.toString() !== sortedColumnOrder.toString());
        setUnsavedColumnOrder(sortedColumnOrder);

        AnalyticsService.track("Drag and Drop", {
          screen: "FeedView",
          page: selectedPage.key,
          sport: sportUrl,
          settings: selectedColumnOrder,
          newSettings: sortedColumnOrder,
          numColumns: sortedColumnOrder.length,
        });
      }
    },
    [sportUrl, selectedPage.key, activeColumnOrder, selectedColumnOrder]
  );

  /** Create user setting ONLY when user exits the walkthrough */
  const createWalkthroughCompletedUserUISetting = async () => {
    await createUserUISetting({
      variables: {
        league: currentSportObj.key,
        settingType: "WEBWALKTHROUGHCOMPLETE",
        settingValue: "true",
      },
      optimisticResponse: {
        createUserUiSetting: {
          __typename: "UserUISetting",
          id: "new-setting",
          parsedValue: true,
          uiSection: null,
          updateTs: moment.utc().toISOString(),
        },
      },
    }).catch(console.warn);
  };

  /** Handle the intro walkthrough */
  const handleJoyrideCallback = useCallback(async (data: CallBackProps) => {
    if (data.action === "start") {
      AnalyticsService.track("Onboarding - Started", {
        screen: "FeedView",
      });
    }

    if (data.action === "skip" && data.lifecycle === "complete") {
      const joyRideSteps: any = {
        0: "Show me Around - Step 1",
        1: "Best Bets Dropdown - Step 2",
        2: "Add/Remove Stats Dropdown - Step3",
        3: "Games Table Column - Step 4",
        4: "Save the Dashboard - Step 5",
      };

      await createWalkthroughCompletedUserUISetting();
      AnalyticsService.track("Onboarding - Skipped", {
        screen: "FeedView",
        endedAt: joyRideSteps[data.index],
      });
    }

    if (data.status === "finished" && data.lifecycle === "complete") {
      await createWalkthroughCompletedUserUISetting();
      AnalyticsService.track("Onboarding - Completed", {
        screen: "FeedView",
      });
    }
  }, []);

  /**
   * Update the table column we're sorting by. If the same column is selected a second time,
   * toggle the direction we are sorting
   *
   * @param sortColumnId {number} the ID of the column we want to sort by
   */
  const onSelectSortColumn = useCallback(
    (sortColumnId: number) => {
      setSelectedSortColumnId(sortColumnId);
      let sortDesc = columnSortDesc;
      if (sortColumnId === selectedSortColumnId) {
        sortDesc = !sortDesc;
      }
      setColumnSortDesc(sortDesc);
    },
    [selectedSortColumnId, columnSortDesc]
  );

  /** Set mobile search filter value */
  const onSearch = useCallback((e: any) => setMobileSearchFilter(e.target.value), []);

  // Attach href's to pages when sport, betType, conference changes
  const pageList = useMemo(() => {
    return BetPageDescriptions.map((page: any) => {
      let href = `/${currentSportObj.url}/${page.path}`;
      if (betTypeUrl) {
        if (betTypeUrl !== currentSportObj.favoriteLine || conferenceUrl) {
          href = `${href}/${betTypeUrl}`;
        }
        if (conferenceUrl) {
          href = `${href}/${conferenceUrl}`;
        }
      } else if (conferenceUrl) {
        href = `${href}/spread/${conferenceUrl}`;
      }
      return {
        ...page,
        href,
      };
    });
  }, [sportUrl, betTypeUrl, conferenceUrl]);

  // Attach href's to conferences when sport, page, betType changes
  const conferenceList = useMemo(() => {
    return (
      currentSportObj.conferenceList?.map((conference, index) => {
        let href = `/${currentSportObj.url}/${pageUrl}`;
        if (betTypeUrl && betTypeUrl !== currentSportObj.favoriteLine) {
          if (index !== 0) {
            href = `${href}/${betTypeUrl}/${conference.url}`;
          } else {
            href = `${href}/${betTypeUrl}`;
          }
        } else if (index !== 0) {
          href = `${href}/spread/${conference.url}`;
        }
        return {
          ...conference,
          href,
        };
      }) ?? []
    );
  }, [sportUrl, pageUrl, betTypeUrl]);

  // Attach href's to betTypes when sport, page, conference changes
  const betTypeList = useMemo(() => {
    return currentSportObj.filters.map((betType) => {
      // NOTE: If the pageKey in the url isn't one of our 4 pages, it is a one off
      // page brian has created for SEO and we just want it to act like an odds page
      const page = Object.keys(PAGES).includes(pageUrl) ? pageUrl : "odds";
      let href = `/${currentSportObj.url}/${page}`;
      if (betType.key !== currentSportObj.favoriteLine || conferenceUrl) {
        href = `${href}/${betType.url}`;
      }
      if (conferenceUrl) {
        href = `${href}/${conferenceUrl}`;
      }
      return {
        ...betType,
        href,
      };
    });
  }, [sportUrl, pageUrl, conferenceUrl]);

  const isTrendingBets = selectedPage.key === "trend";

  return (
    <React.Fragment>
      <LiveGameBar sport={currentSportObj} />
      <div className="games-grid">
        <div
          className="games-header"
          style={{
            backgroundImage: `linear-gradient(0deg, #00214D88, #00214D88), url(/heros/${currentSportObj.key}.jpg)`,
          }}
        >
          <h1 className="games-header-h1">{pageH1}</h1>
          {pageHeadline ? <div className="games-header-headline">{renderRichText(pageHeadline)}</div> : null}
        </div>

        <NextSeo
          title={pageTitle}
          description={pageDescription}
          openGraph={{
            title: pageTitle,
            description: pageDescription,
            url: `https://betql.co/${sportUrl}/${pageUrl}`,
            images: [ogImage],
          }}
          canonical={noindex ? undefined : `https://betql.co${asPath}`}
          noindex={noindex}
        />
        <Drawer
          ref={drawerRef}
          sportUrl={sportUrl}
          settingList={settingList}
          activeBookId={activeBookId}
          activeColumnOrder={activeColumnOrder}
          activeDashboardName={activeDashboardName}
          selectedPage={selectedPage}
          selectedBetType={selectedBetType}
          userBookSetting={userBookSetting}
          userColumnOrderSetting={userColumnOrderSetting}
          setDashName={setDashName}
          saveSetting={saveSetting}
          updateSetting={updateSetting}
          deleteSetting={deleteSetting}
          selectSetting={selectSetting}
        />
        <div className="games-view">
          <div className="d-none d-sm-flex games-view__filter-container">
            <div className="games-view__filter-container-content">
              <div className="games-view__page-container">
                {!isTrendingBets ? (
                  <div className="games-view__dashboard-setting-folder-icon" onClick={openDrawer}>
                    {unsavedSettings ? <div className="games-view__dashboard-setting-folder-dot" /> : null}
                    <Icon path={mdiFolderOutline} size="22px" color="#00214d" />
                  </div>
                ) : null}
                <Dropdown
                  className="games-view__page-dropdown"
                  onSelectItem={onSelectPage}
                  selectedIndex={PAGES[pageUrl] || 0}
                  items={pageList}
                />
              </div>
              <Dropdown
                className="games-view__filter-dropdown"
                onSelectItem={onSelectBetType}
                selectedIndex={selectedBetTypeIndex}
                items={betTypeList}
              />
              {sportUrl.includes("ncaa") ? (
                <Dropdown
                  className="games-view__conference-dropdown"
                  selectedIndex={selectedConferenceIndex}
                  items={conferenceList}
                />
              ) : null}
              {hasValidConfiguredSchedule ? (
                <DropdownWithButtons
                  className="games-view__page-dropdown-schedule"
                  onSelectItem={onSchduleSelection}
                  onSelectRightChevron={onSelectRightChevron}
                  onSelectLeftChevron={onSelectLeftChevron}
                  selectedIndex={selectedWeekIndex}
                  items={currentSchedule}
                />
              ) : (
                <DatePicker
                  className="games-view__date-picker"
                  selectedDate={selectedDate}
                  onSelectDate={onSelectedDate}
                />
              )}
              {!isTrendingBets ? (
                <ModifyStatsDropdown
                  activeBookId={activeBookId}
                  activeColumnOrder={activeColumnOrder}
                  books={bookList}
                  sportUrl={sportUrl}
                  betType={selectedBetType.key}
                  onSave={onSaveChanges}
                  onCancel={onCancelChanges}
                  className="games-view__advanced-dropdown"
                  trackingProps={{
                    betType: selectedBetType.key,
                    book: selectedBook.name,
                    sport: sportUrl,
                    page: selectedPage.key,
                  }}
                />
              ) : null}
            </div>
          </div>
          <div className="d-sm-none games-view__mobile-filter-container">
            <div className="games-view__mobile-filter-first-row">
              <div className="games-view__mobile-page-dropdown-container">
                <Dropdown onSelectItem={onSelectPage} selectedIndex={selectedPageIndex} items={pageList} />
              </div>
              <div className="games-view__mobile-page-dropdown-schedule-container">
                {hasValidConfiguredSchedule ? (
                  <DropdownWithButtons
                    className="games-view__mobile-page-dropdown-schedule"
                    onSelectItem={onSchduleSelection}
                    onSelectRightChevron={onSelectRightChevron}
                    onSelectLeftChevron={onSelectLeftChevron}
                    selectedIndex={selectedWeekIndex}
                    items={currentSchedule}
                  />
                ) : (
                  <DatePicker
                    className="games-view__mobile-date-picker"
                    selectedDate={selectedDate}
                    onSelectDate={onSelectedDate}
                  />
                )}
              </div>
            </div>
            <ButtonGroup
              className="games-view__mobile-filter-button-group"
              onSelectItem={onSelectBetType}
              selectedIndex={selectedBetTypeIndex}
              items={betTypeList}
            />
          </div>
          <div className="games-view__mobile-last-row">
            {sportUrl.includes("ncaa") ? (
              <Dropdown
                className="games-view__mobile-conference-dropdown"
                selectedIndex={selectedConferenceIndex}
                items={conferenceList}
              />
            ) : (
              <Input
                className="games-view__mobile-conference-dropdown"
                type="text"
                placeholder="Search"
                onChange={onSearch}
              />
            )}
            <div className="games-view__mobile-sorting-button">
              <button
                className={`games-view__date-button ${
                  mobileSortIndex === 0 ? "games-view__active-sort-button" : "games-view__not-active-sort-button"
                }`}
                onClick={() => setMobileSortIndex(0)}
              >
                {mobileSortIndex === 0 ? (
                  <Icon path={mdiClock} size="18px" color="#eaeff4" />
                ) : (
                  <Icon path={mdiClockOutline} size="18px" color="#00214D" />
                )}
              </button>
              <button
                className={`games-view__star-button ${
                  mobileSortIndex === 1 ? "games-view__active-sort-button" : "games-view__not-active-sort-button"
                }`}
                onClick={() => setMobileSortIndex(1)}
              >
                {mobileSortIndex === 1 ? (
                  <Icon path={mdiStar} size="18px" color="#eaeff4" />
                ) : (
                  <Icon path={mdiStarOutline} size="18px" color="#00214D" />
                )}
              </button>
            </div>
          </div>

          {!isTrendingBets ? (
            <ModifyStatsDropdown
              activeBookId={activeBookId}
              activeColumnOrder={activeColumnOrder}
              books={bookList}
              sportUrl={currentSportObj.url}
              betType={selectedBetType.key}
              onSave={onSaveChanges}
              onCancel={onCancelChanges}
              className="d-sm-none"
              trackingProps={{
                betType: selectedBetType.key,
                book: selectedBook.name,
                sport: sportUrl,
                page: selectedPage.key,
              }}
            />
          ) : null}

          <a id="H2" className="d-sm-none" />
          {mobileSearchFilter !== "" && !events.length ? (
            <div className="games-view__no-games-found">
              <h4>No Games Found</h4>
            </div>
          ) : (
            <GamesTable
              style={{ marginBottom: "100px" }}
              games={events}
              currentSportObj={currentSportObj}
              filterKey={selectedBetType.key}
              pageKey={selectedPage.key}
              selectedSortColumnId={selectedSortColumnId}
              columnSortDesc={columnSortDesc}
              onSelectSortColumn={onSelectSortColumn}
              trackingProps={{
                betType: selectedBetType.key,
                book: selectedBook.name,
                sport: sportUrl,
                page: selectedPage.key,
              }}
              onDragEnd={onDragEnd}
              columnOrder={activeColumnOrder}
              adEvents={adEvents}
              pageValue={selectedPage.name}
            />
          )}
          {settingsLoading && !settingsData ? (
            <p className="games-view__status-text">Loading your saved dashboards...</p>
          ) : !settingsLoading && settingsError && !settingsData ? (
            <p className="games-view__status-text games-view__status-text--error">
              Error loading your saved dashboards...
            </p>
          ) : eventsLoading && events?.length ? (
            <p className="games-view__status-text">Updating sporting events...</p>
          ) : eventsLoading && !events?.length ? (
            <p className="games-view__status-text">Loading sporting events...</p>
          ) : eventsError ? (
            <p className="games-view__status-text games-view__status-text--error">Error loading sporting events</p>
          ) : !eventsError && !events?.length && !eventsLoading ? (
            <p className="games-view__status-text">No Events</p>
          ) : (
            <p className="games-view__status-text" />
          )}
          {showProductTour && (
            <Joyride
              callback={handleJoyrideCallback}
              continuous={true}
              showSkipButton={true}
              steps={isMobile ? MOBILE_STEPS : DESKTOP_STEPS}
              disableOverlayClose={true}
              hideBackButton={true}
              locale={{
                next: "Next",
                last: "Done",
                skip: "End tour",
              }}
              styles={{
                options: {
                  zIndex: 10000,
                  arrowColor: "#004198",
                  backgroundColor: "#004198",
                  primaryColor: "#ffffff",
                  textColor: "#ffffff",
                  overlayColor: "rgba(0, 0, 0, 0.2)",
                },
              }}
            />
          )}
          {seoContent?.text && <div className="games-view__bet-info-container">{renderRichText(seoContent.text)}</div>}
        </div>
        <GamesRail currentSportObj={currentSportObj} />
        <style jsx global>
          {styles}
        </style>
      </div>
      <OfferCarouselModal />
    </React.Fragment>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const regularPaths = buildStaticPathsForSportsBettingPage(Object.keys(WEB_SPORTS_MAP), Object.keys(PAGES));
  const ppcPaths = buildStaticPathsForSportsBettingPage(PPCSportsList, PPCPagesList);

  return {
    paths: [...regularPaths, ...ppcPaths],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps<GamesViewProps> = async ({ params }) => {
  const sport = getStringQueryParam(params?.sport);
  const pageParams = getStringArrayQueryParam(params?.page);
  // since this is the [...page] we handle multiple params here
  // which could be the page/filter?/conference?
  // page array position is [page, filter, conference]
  const { 0: page, 1: filter = null, 2: conference = null } = pageParams;
  const currentSportObj = WEB_SPORTS_MAP[sport];
  if (!currentSportObj) {
    return {
      notFound: true,
    };
  }
  const seoQuery = isPpcPage(page) ? PAID_LANDING_PAGES_SEO_QUERY : GAMES_TABLE_SEO_QUERY;
  const seoContentResponse = await initializeApollo().query<PrismicGamesTableContent | PrismicPaidLandingPageContent>({
    query: seoQuery,
    fetchPolicy: "network-only",
    variables: {
      uid: currentSportObj.url ?? "",
    },
  });

  const schedule = await getSchedule(currentSportObj.lookupKey);
  return {
    props: {
      schedule: schedule,
      seoContent: getSEOContent(seoContentResponse, page as string, filter as string | undefined),
      sportUrl: sport,
      pageUrl: page,
      betTypeUrl: filter ?? null,
      conferenceUrl: conference ?? null,
    },
    revalidate: 60 * 60 * 24, // daily
  };
};

export default Games;
